import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Code, Zap, Webhook, Book, ArrowRight, Terminal, Braces, Activity } from 'lucide-react';
import { mockData } from '../data/mock';

const DeveloperSection = () => {
  const { developer } = mockData;

  const iconMap = {
    'REST API': Braces,
    'WebSocket': Activity,
    'Webhooks': Webhook,
    'SDKs': Terminal
  };

  const codeExample = `// Initialize Winnex SDK
import { WinnexClient } from '@winnex/sdk';

const client = new WinnexClient({
  apiKey: 'your-api-key',
  environment: 'sandbox'
});

// Fetch available games
const games = await client.games.list({
  provider: 'pragmatic-play',
  jurisdiction: 'MGA'
});

console.log(\`Found \${games.length} games\`);`;

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4">
            Developer Experience
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {developer.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {developer.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Left side - SDKs and features */}
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {developer.sdks.map((sdk, index) => {
                const IconComponent = iconMap[sdk.name];
                return (
                  <Card key={index} className="group hover:shadow-md transition-all duration-300 border-0 shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="p-2 bg-blue-100 rounded-lg w-fit group-hover:bg-blue-200 transition-colors">
                        <IconComponent className="w-6 h-6 text-blue-600" />
                      </div>
                      <CardTitle className="text-lg">{sdk.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-600">
                        {sdk.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">&lt; 5 min</div>
                <div className="text-sm text-gray-600">Integration time</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">99.9%</div>
                <div className="text-sm text-gray-600">API uptime</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">24/7</div>
                <div className="text-sm text-gray-600">Support</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                <Book className="w-5 h-5 mr-2" />
                View Documentation
              </Button>
              <Button size="lg" variant="outline">
                <Code className="w-5 h-5 mr-2" />
                Try Sandbox
              </Button>
            </div>
          </div>

          {/* Right side - Code example */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl" />
            <Card className="relative bg-gray-900 border-0 overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full" />
                    <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                    <div className="w-3 h-3 bg-green-400 rounded-full" />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    quickstart.js
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code className="language-javascript">
                    {codeExample}
                  </code>
                </pre>
              </CardContent>
            </Card>

            {/* Floating elements */}
            <div className="absolute -top-6 -right-6 p-4 bg-white rounded-xl shadow-lg border">
              <div className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium">Live API</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to start building?
            </h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Get your API keys, explore our comprehensive documentation, 
              and join thousands of developers building the future of gaming.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Get API Keys
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline">
                Join Developer Discord
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeveloperSection;